type Dict = Record<string, string>;

const en: Dict = {
  "meta.title": "BOOSTSCIENCE Observatory | Skills, Careers & Emerging Trends in Natural Sciences",
  "meta.description": "Explore emerging skills, technologies, career pathways and learning opportunities in Biology and Chemistry through the BOOSTSCIENCE Observatory.",
  "meta.ogTitle": "BOOSTSCIENCE Observatory",
  "meta.ogDescription": "Explore the skills, technologies and career pathways shaping the future of Biology and Chemistry.",
  "meta.radar.title": "Skills & Trends Radar | BOOSTSCIENCE Observatory",
  "meta.match.title": "Career & Skills Match | BOOSTSCIENCE Observatory",
  "meta.method.title": "Observatory Methodology | BOOSTSCIENCE Observatory",
  "meta.account.title": "My results | BOOSTSCIENCE Observatory",
  "brand.observatory": "BOOSTSCIENCE Observatory",
  "brand.subtitle": "Skills, Careers & Emerging Trends in Natural Sciences",
  "brand.project": "Boosting Education Quality and Entrepreneurial Mindsets for Natural Sciences in Ukraine",
  "nav.home": "Overview",
  "nav.radar": "Skills Radar",
  "nav.match": "Career Match",
  "nav.methodology": "Methodology",
  "nav.skip": "Skip to Observatory content",
  "crumb.label": "Breadcrumb",
  "crumb.home": "Home",
  "hero.eyebrow": "BOOSTSCIENCE OBSERVATORY",
  "hero.heading": "Explore the skills shaping the future of Biology & Chemistry",
  "hero.body": "Discover how emerging technologies, competencies and career pathways connect for Biology and Chemistry — including Ukrainian Higher Education, virtual laboratories and university–industry learning.",
  "hero.ctaRadar": "Explore Skills & Trends",
  "hero.ctaMatch": "Assess My Skills",
  "hero.ctaMethod": "View methodology",
  "flow.label": "Observatory path",
  "flow.explore": "EXPLORE",
  "flow.explore.detail": "emerging trends",
  "flow.understand": "UNDERSTAND",
  "flow.understand.detail": "skills & careers",
  "flow.develop": "DEVELOP",
  "flow.develop.detail": "your competencies",
  "tools.kicker": "CHOOSE YOUR PATH",
  "tools.title": "What would you like to do?",
  "tools.body": "Explore the changing Natural Sciences landscape or assess the skills relevant to your career interests.",
  "tools.radar.kicker": "01 · EXPLORE",
  "tools.radar.title": "Skills & Trends Radar",
  "tools.radar.body": "Discover emerging technologies, skills and career pathways across Biology and Chemistry — and explore how they connect.",
  "tools.radar.m1": "Emerging Trends",
  "tools.radar.m2": "Skills Connections",
  "tools.radar.m3": "Career Pathways",
  "tools.radar.cta": "Explore the Radar",
  "tools.match.kicker": "02 · ASSESS",
  "tools.match.title": "Career & Skills Match",
  "tools.match.body": "Choose a professional pathway, assess your current competencies and identify the skills that matter most for your development.",
  "tools.match.m1": "3–5 min assessment",
  "tools.match.m2": "Personal skills profile",
  "tools.match.m3": "Learning recommendations",
  "tools.match.cta": "Assess My Skills",
  "discover.eyebrow": "Interactive preview",
  "discover.title": "See how scientific areas connect",
  "discover.body": "Browse a simplified view of the catalog, then open the full Radar to explore every connection.",
  "discover.tabs": "Discipline",
  "discover.area": "Selected area",
  "discover.cta": "Explore full Radar",
  "model.eyebrow": "Knowledge model",
  "model.title": "How the Observatory connects learning and work",
  "model.body": "The catalog links education, competencies and scientific technologies to career pathways and labour-market relevance — then to BOOSTSCIENCE learning.",
  "model.education": "Education",
  "model.skills": "Skills",
  "model.technologies": "Technologies",
  "model.careers": "Career pathways",
  "model.labour": "Labour-market relevance",
  "model.gap": "Skills gap",
  "model.learning": "BOOSTSCIENCE learning",
  "model.development": "Skills development",
  "metrics.eyebrow": "Catalog snapshot",
  "metrics.title": "What is mapped today",
  "evidence.eyebrow": "Transparent by design",
  "evidence.title": "Evidence behind the Observatory",
  "evidence.body": "Every published record identifies its source, reference period and last review. The Observatory is structured, traceable and designed to evolve with BOOSTSCIENCE.",
  "evidence.traceable": "Traceable",
  "evidence.traceable.body": "Sources, review dates and evidence levels sit on each published entity.",
  "evidence.structured": "Structured",
  "evidence.structured.body": "Disciplines, technologies, skills, careers and resources share one data model.",
  "evidence.evolving": "Evolving",
  "evidence.evolving.body": "The catalog is reviewed as project evidence and partner expertise grow.",
  "evidence.cta": "Explore methodology",
  "learn.eyebrow": "BOOSTSCIENCE learning",
  "learn.title": "From exploration to skills development",
  "learn.body": "Use the Observatory to discover priority areas, assess your profile, and follow BOOSTSCIENCE resources — published deliverables now, module and virtual-lab links as they are released.",
  "learn.discover": "Discover",
  "learn.discover.body": "Explore emerging areas, skills and career pathways in the Radar.",
  "learn.assess": "Assess",
  "learn.assess.body": "Build a Skills Match profile against a selected pathway.",
  "learn.develop": "Develop",
  "learn.develop.body": "Follow recommended BOOSTSCIENCE learning activities for identified gaps.",
  "learn.soon": "Module, virtual-lab and MOOC links will appear here as BOOSTSCIENCE publishes them. Public D1 deliverables are already available.",
  "learn.available": "Available now",
  "learn.coming": "In development",
  "learn.open": "Open",
  "context.eyebrow": "Project context",
  "context.title": "Built for BOOSTSCIENCE, not as a generic skills widget",
  "context.body": "The catalog follows the project’s four pillars — Understand, Design, Develop, Deliver — and the consortium of 12 organisations in Greece, Spain and Ukraine, including five Ukrainian universities and pharmaceutical, drug-discovery and materials-research partners.",
  "context.m1": "Master-level module in applied chemical and biomolecular analysis",
  "context.m2": "Virtual laboratories and VLE / MOOC delivery",
  "context.m3": "University–industry pathways in pharma, biotech and materials",
  "method.pillars.title": "Project methodology",
  "method.pillars.body": "BOOSTSCIENCE is organised as Understand (needs analysis), Design (curriculum), Develop (VLE, digital content and virtual labs) and Deliver (pilot, capacity building and sustainability). The Observatory currently supports Understand and Design with an expert-curated taxonomy. WP2 survey evidence is not yet imported.",
  "method.pillar.understand": "Understand",
  "method.pillar.understand.body": "Map needs in Biology and Chemistry Higher Education, including Ukrainian partner universities, and structure the skills landscape the Observatory publishes.",
  "method.pillar.design": "Design",
  "method.pillar.design.body": "Inform curriculum and competence design for the master-level module in applied chemical and biomolecular analysis.",
  "method.pillar.develop": "Develop",
  "method.pillar.develop.body": "Prepare digital content, virtual laboratories and VLE / MOOC delivery. Observatory links to those activities appear as partners publish them.",
  "method.pillar.deliver": "Deliver",
  "method.pillar.deliver.body": "Support pilots, capacity building and sustainability. The Observatory remains an exploration tool, not a live labour-market dashboard.",
  "final.title": "Start exploring the skills landscape",
  "final.body": "Open the Radar to browse connections, or complete a short Skills Match to identify development priorities.",
  "method.heading": "Evidence-informed, transparent and evolving",
  "method.body": "The Observatory structures information from BOOSTSCIENCE research activities, project partners, competence frameworks and selected external sources. Each dataset identifies its source, reference period and last review.",
  "method.cta": "View methodology",
  "footer.notice": "Developed within BOOSTSCIENCE — Boosting Education Quality and Entrepreneurial Mindsets for Natural Sciences in Ukraine.",
  "radar.title": "Skills & Trends Radar",
  "radar.body": "Explore technologies, skills and professional pathways relevant to Biology and Chemistry. Select an entry to inspect connections, evidence and related learning.",
  "radar.search": "Search skills, technologies or careers",
  "radar.viewRadar": "Map",
  "radar.viewList": "List",
  "radar.connections": "Show connections",
  "radar.filters": "Filters",
  "radar.clear": "Clear all",
  "radar.discipline": "Discipline",
  "radar.view": "View",
  "radar.trend": "Trend",
  "radar.geo": "Geography",
  "radar.all": "All",
  "radar.biology": "Biology",
  "radar.chemistry": "Chemistry",
  "radar.interdisciplinary": "Interdisciplinary",
  "radar.technologies": "Technologies",
  "radar.skills": "Skills",
  "radar.careers": "Careers",
  "radar.sectors": "Sectors",
  "radar.emerging": "Emerging",
  "radar.growing": "Growing",
  "radar.established": "Established",
  "radar.transforming": "Transforming",
  "radar.europe": "Europe",
  "radar.ukraine": "Ukraine",
  "radar.geoAll": "All available",
  "radar.core": "Core",
  "radar.ringGrowing": "Growing",
  "radar.ringEmerging": "Emerging",
  "radar.empty": "No entries match the selected filters.",
  "radar.searchEmpty": "No matching skills, technologies or careers were found. Try a broader search.",
  "radar.close": "Close details",
  "radar.why": "Why it matters",
  "radar.relatedSkills": "Related skills",
  "radar.relatedCareers": "Related careers",
  "radar.relatedSectors": "Relevant sectors",
  "radar.relatedTech": "Related technologies",
  "radar.connection": "BOOSTSCIENCE connection",
  "radar.evidence": "Evidence",
  "radar.source": "Source",
  "radar.period": "Reference period",
  "radar.coverage": "Geographical coverage",
  "radar.reviewed": "Last reviewed",
  "radar.assess": "Assess my skills for related careers",
  "radar.exploreMatch": "Open Career & Skills Match",
  "radar.col.name": "Name",
  "radar.col.type": "Type",
  "radar.col.discipline": "Discipline",
  "radar.col.trend": "Trend",
  "radar.col.skills": "Related skills",
  "radar.col.skillsHint": "For skills, other catalog skills in this view that share a career or technology (same links as the map). Trend and search do not shrink this count. For technologies, careers and sectors, catalog-declared related skills.",
  "radar.col.careers": "Related careers",
  "radar.col.source": "Evidence / source",
  "radar.metric.skills": "Skills mapped",
  "radar.metric.careers": "Career pathways",
  "radar.metric.emerging": "Emerging areas",
  "radar.metric.sources": "Sources reviewed",
  "radar.metric.resources": "Published resources",
  "radar.group": "Skill group",
  "radar.dense": "Labels hidden while this view is crowded — open an entry or switch to List.",
  "radar.count": "{n} entries",
  "radar.viewMode": "View mode",
  "radar.ringGuide": "Concentric rings from the centre: core (established), growing, then emerging.",
  "radar.catalogNote": "Figures are counts from the published Observatory catalog. They are not live labour-market statistics.",
  "radar.mobileList": "List view is used on this screen so entries stay readable.",
  "radar.help.title": "How to use the Radar",
  "radar.help.body": "Choose a view, then select a node or list row. The panel shows definition, related skills and careers, and the evidence behind the record.",
  "radar.help.filter": "Use Discipline, View, Skill group, Trend and Geography to narrow the landscape.",
  "radar.help.select": "Nothing selected yet.",
  "match.title": "Career & Skills Match",
  "match.body": "Choose a professional pathway and complete a short self-assessment to identify strengths and priority competencies.",
  "match.start": "Start Assessment",
  "match.disclaimer": "Educational guidance based on self-assessment. Not a recruitment test, formal qualification or professional certification.",
  "privacy.notice.title": "Privacy and your Observatory profile",
  "privacy.notice.body": "With your consent, BOOSTSCIENCE stores your email and the Observatory context needed to continue your journey. Newsletter consent is separate and optional. We do not store names, phone numbers, CVs, university IDs, or the individual 0–4 skill answers. If you later create an account, only the selected pathway, scores and generated profile are saved for retrieval.",
  "privacy.notice.link": "Privacy policy",
  "match.privacy": "Your email is collected with consent as part of the Observatory journey. Newsletter consent is separate and optional. Individual 0–4 skill answers remain in your browser and are not stored as lead data.",
  "match.startOver": "Start over",
  "match.continue": "Continue",
  "match.back": "Back",
  "match.seeResults": "See results",
  "match.step.field": "Field",
  "match.step.career": "Career",
  "match.step.skills": "Skills",
  "match.step.priorities": "Priorities",
  "match.step.results": "Results",
  "match.field.q": "What is your main field?",
  "match.stage.q": "What stage best describes you? (optional)",
  "match.stage.note": "This adjusts suggested pathways (for example Academic staff). It does not change the Skills Match calculation.",
  "match.stage.masters-student": "Master's student",
  "match.stage.recent-graduate": "Recent graduate",
  "match.stage.doctoral-candidate": "Doctoral candidate",
  "match.stage.researcher": "Researcher",
  "match.stage.academic-staff": "Academic staff",
  "match.stage.other": "Other",
  "match.career.q": "Which professional area interests you most?",
  "match.career.examples": "Example pathways",
  "match.career.select": "Select a pathway to assess",
  "match.skills.q": "How would you rate these competencies today?",
  "match.skills.help": "Rate one competency at a time from 0 (not familiar yet) to 4 (strong / can apply independently). Your progress is saved in this browser while you complete the assessment.",
  "match.scale.0": "Not familiar yet",
  "match.scale.1": "Basic awareness",
  "match.scale.2": "Developing",
  "match.scale.3": "Confident",
  "match.scale.4": "Strong / independent",
  "match.incomplete": "Please rate every listed skill before continuing.",
  "match.priorities.q": "What would you most like to strengthen?",
  "match.priorities.help": "Choose up to three focus areas. This personalises recommendations and does not replace the Skills Match calculation.",
  "match.results.title": "Your BOOSTSCIENCE Skills Profile",
  "match.results.pathway": "Selected pathway",
  "match.results.score": "Skills Match",
  "match.results.band.strong": "Strong alignment",
  "match.results.band.good": "Good foundation",
  "match.results.band.developing": "Developing alignment",
  "match.results.band.priority": "Priority development areas identified",
  "match.results.lead.strong": "Your self-assessment indicates strong alignment with this pathway across the competencies reviewed.",
  "match.results.lead.good": "Your self-assessment indicates a solid foundation for this pathway, with selected competencies offering the strongest development opportunities.",
  "match.results.lead.developing": "Your self-assessment indicates developing alignment. Several competencies are useful priorities for further practice.",
  "match.results.lead.priority": "Your self-assessment highlights priority development areas for this pathway. Treat the list below as a learning agenda, not a judgement of potential.",
  "match.results.strengths": "Current strengths",
  "match.results.strengths.body": "These competencies are comparatively well aligned with the selected pathway based on your self-assessment.",
  "match.results.priorities": "Priority skills to develop",
  "match.results.related": "Related career pathways",
  "match.results.path": "Your BOOSTSCIENCE Learning Path",
  "match.results.sequence": "Suggested development sequence",
  "match.results.print": "Print / Save as PDF",
  "match.results.radar": "Explore Skills Radar",
  "match.results.exploreSkill": "Explore in Radar",
  "match.comingSoon": "Coming soon",
  "match.noResources": "BOOSTSCIENCE learning resources related to this skill will be added as module content becomes available.",
  "match.priority.high": "High priority",
  "match.priority.medium": "Medium priority",
  "match.noVacancies": "This is not a live vacancies list.",
  "method.pageTitle": "Observatory Methodology",
  "method.purpose.title": "Purpose",
  "method.purpose.body": "The Observatory supports exploration of skills, technologies and career pathways relevant to Biology and Chemistry in BOOSTSCIENCE. It helps master’s students, academic staff and partners see connections between Higher Education, competencies and labour-market-relevant areas without claiming live employment data.",
  "method.model.title": "Data model",
  "method.model.body": "Information is structured as discipline → domain → technology → skill → career → learning resource, with sources attached to each published entity.",
  "method.framework.title": "Skills framework",
  "method.framework.body": "The taxonomy is informed by BOOSTSCIENCE project research, partner expertise, and selected competence frameworks: ResearchComp, DigComp, EntreComp and GreenComp. Mappings are aligned with or informed by those frameworks. They are not certifications and do not imply official endorsement.",
  "method.scoring.title": "Skills Match",
  "method.scoring.body": "Results are generated from career–skill mappings, importance weights (1–5), target levels (1–4) and your self-assessment (0–4). For each skill, achievementRatio = min(userLevel / targetLevel, 1). The Skills Match is the importance-weighted average of those ratios, expressed as a percentage. It is not an employability probability or a hiring score.",
  "method.limits.title": "Limitations",
  "method.limits.body": "Self-assessment is subjective. Labour-market conditions evolve. Information should be reviewed periodically. Results are educational guidance, not professional certification, and do not guarantee employment. Trend labels are qualitative classifications, not demand statistics.",
  "method.sources.title": "Data sources",
  "method.update.title": "Last review",
  "method.update.body": "Catalog last reviewed: {date}.",
  "error.load": "We couldn't load this information. Please try again.",
  "error.loading": "Loading Observatory catalog…",
  "error.retry": "Retry",
  "teaser.eyebrow": "BOOSTSCIENCE OBSERVATORY",
  "teaser.heading": "Explore the future of skills in Biology & Chemistry",
  "teaser.body": "Discover emerging technologies, explore career pathways and identify the competencies shaping Natural Sciences.",
  "teaser.cta": "Explore Observatory",
  "teaser.match": "Assess My Skills",
  "a11y.chart": "Current view: {discipline}, {trend} {type}. {count} entries displayed. {connected}",
  "group.scientific-lab": "Scientific & Laboratory",
  "group.digital-data": "Digital & Data",
  "group.research": "Research",
  "group.green-sustainability": "Green & Sustainability",
  "group.entrepreneurial-innovation": "Entrepreneurial & Innovation",
  "group.transferable-professional": "Transferable & Professional",
  "account.login": "Log in",
  "account.logout": "Log out",
  "account.create": "Create account",
  "account.myResults": "My results",
  "account.myResults.body": "Create an account or sign in with Google to retrieve Skills Profiles you saved after Career & Skills Match, and to download a formatted PDF. We only keep a login identifier for that retrieval.",
  "account.email": "Email",
  "account.password": "Password (8+ characters)",
  "account.mode.label": "Account",
  "account.consent": "I agree that BOOSTSCIENCE keeps a login identifier (email or Google account id) and this Skills Profile (selected pathway and scores) so I can retrieve it and generate a PDF. See the",
  "account.consent.required": "Please confirm the privacy consent before continuing.",
  "account.privacyLink": "privacy policy",
  "account.google": "Continue with Google",
  "account.google.or": "or use email and password",
  "account.google.setup": "Google sign-in is not connected yet. Use email and password, or ask the site administrator to add a Google client ID in Settings → Observatory.",
  "account.google.cancelled": "Google sign-in was cancelled.",
  "account.save.title": "Save this Skills Profile",
  "account.save.body": "Your email has already been captured for this Observatory journey. Creating an account or signing in with Google is optional and only needed if you want to retrieve saved profiles later.",
  "account.save.loggedIn": "Signed in as {email}. Save this profile to download it again later.",
  "account.save.action": "Save profile",
  "account.save.done": "Saved. Open the formatted profile to download or print a PDF.",
  "account.downloadPdf": "Download PDF",
  "account.signedIn": "Signed in as {email}.",
  "account.empty": "No saved Skills Profiles yet. Complete Career & Skills Match, then save from the results page.",
  "account.error": "Something went wrong. Please try again.",
  "account.unavailable": "Account save is available on the published Observatory at boostscience.eu.",
  "account.print.kicker": "Observatory · Career & Skills Match",
  "account.print.disclaimer": "Educational guidance based on self-assessment. Not a recruitment test, formal qualification or professional certification. The Skills Match is an importance-weighted alignment of your ratings with the selected pathway. It is not an employability probability or a hiring score. We do not store names, phone numbers, CVs, university IDs, or the 0–4 skill answers. A login identifier is kept only if you created an account.",
  "flow.radar.aria": "Radar setup progress",
  "flow.radar.title": "Skills & Trends Radar",
  "flow.radar.step": "Step {n} of 4",
  "flow.radar.hero": "Explore the Natural Sciences landscape",
  "flow.radar.hero.body": "Choose the discipline you want to explore first. You can change it later inside the Radar.",
  "flow.radar.choose": "Choose what to explore",
  "flow.radar.choose.q": "What do you want to see first?",
  "flow.radar.skills.body": "Competencies used across scientific and professional pathways.",
  "flow.radar.technologies.body": "Scientific and digital areas changing Biology and Chemistry.",
  "flow.radar.careers.body": "Professional pathways and the competencies connected to them.",
  "flow.radar.objective": "Your objective",
  "flow.radar.objective.q": "What would be most useful to you?",
  "flow.radar.intent.trends": "Understand emerging trends",
  "flow.radar.intent.career": "Explore career pathways",
  "flow.radar.intent.skills": "Identify relevant skills",
  "flow.radar.ready": "Your Radar is ready",
  "flow.radar.ready.body": "Enter your email to open your personalised Radar view and keep your BOOSTSCIENCE Observatory journey connected to you.",
  "flow.match.aria": "Assessment progress",
  "flow.match.title": "Career & Skills Match",
  "flow.match.step": "Step {current} of {total}",
  "flow.match.hero": "Build your Natural Sciences skills profile",
  "flow.match.hero.body": "Choose your field first. We will adapt the pathways and questions to your interests.",
  "flow.match.about": "About you",
  "flow.match.about.q": "Where are you in your professional journey?",
  "flow.match.about.body": "This only improves pathway suggestions. It does not change the Skills Match calculation.",
  "flow.match.direction": "Career direction",
  "flow.match.direction.q": "Which professional area interests you most?",
  "flow.match.pathway": "Choose a pathway",
  "flow.match.ready": "Your profile is taking shape",
  "flow.match.ready.body": "Enter your email to continue the assessment and keep your personalised BOOSTSCIENCE Skills Profile connected to you.",
  "flow.match.competency": "Competency {current} of {total}",
  "flow.match.competency.q": "How confident are you applying this competency?",
  "flow.match.toPriorities": "Continue to priorities",
  "flow.match.final": "Final step",
  "flow.match.viewProfile": "View my profile",
  "flow.match.profileEyebrow": "YOUR BOOSTSCIENCE SKILLS PROFILE",
  "lead.step": "Your personalised experience is ready",
  "lead.continueTitle": "Continue with your email",
  "lead.continueBody": "Enter your email to continue and keep your BOOSTSCIENCE Observatory journey connected to you.",
  "lead.email": "Email",
  "lead.placeholder": "you@example.com",
  "lead.privacy": "I agree that BOOSTSCIENCE processes my email to provide and store my Observatory journey. See the privacy policy.",
  "lead.marketing": "I would also like to receive BOOSTSCIENCE news and Observatory updates.",
  "lead.error.consent": "Please confirm the privacy consent to continue.",
  "lead.error.email": "Enter a valid email address.",
  "lead.error.save": "We couldn't save your email. Please try again.",
  "lead.saving": "Saving…",
  "lead.note": "No password is required. Newsletter consent is optional.",
};

const uk: Dict = {
  "meta.title": "Обсерваторія BOOSTSCIENCE | Навички, кар'єра та нові тенденції в природничих науках",
  "meta.description": "Дослідіть нові навички, технології, кар'єрні шляхи та можливості навчання в біології та хімії за допомогою Обсерваторії BOOSTSCIENCE.",
  "meta.ogTitle": "Обсерваторія BOOSTSCIENCE",
  "meta.ogDescription": "Дослідіть навички, технології та кар'єрні шляхи, що формують майбутнє біології та хімії.",
  "meta.radar.title": "Радар навичок та тенденцій | Обсерваторія BOOSTSCIENCE",
  "meta.match.title": "Зіставлення кар'єри та навичок | Обсерваторія BOOSTSCIENCE",
  "meta.method.title": "Методологія обсерваторії | Обсерваторія BOOSTSCIENCE",
  "meta.account.title": "Мої результати | Обсерваторія BOOSTSCIENCE",
  "brand.observatory": "Обсерваторія BOOSTSCIENCE",
  "brand.subtitle": "Навички, кар'єра та нові тенденції в природничих науках",
  "brand.project": "Підвищення якості освіти та підприємницького мислення для природничих наук в Україні",
  "nav.home": "Огляд",
  "nav.radar": "Радар навичок",
  "nav.match": "Зіставлення кар'єри",
  "nav.methodology": "Методологія",
  "nav.skip": "Перейти до змісту обсерваторії",
  "crumb.label": "Навігаційний шлях",
  "crumb.home": "Головна",
  "hero.eyebrow": "ОБСЕРВАТОРІЯ BOOSTSCIENCE",
  "hero.heading": "Дослідіть навички, що формують майбутнє біології та хімії",
  "hero.body": "Дізнайтеся, як новітні технології, компетенції та кар'єрні шляхи пов'язані для біології та хімії, включаючи вищу освіту в Україні, віртуальні лабораторії та навчання в університетах та промисловості.",
  "hero.ctaRadar": "Дослідіть навички та тенденції",
  "hero.ctaMatch": "Оцініть мої навички",
  "hero.ctaMethod": "Переглянути методологію",
  "flow.label": "Шлях обсерваторії",
  "flow.explore": "ДОСЛІДЖУЙТЕ",
  "flow.explore.detail": "нові тенденції",
  "flow.understand": "РОЗУМІЙТЕ",
  "flow.understand.detail": "навички та кар'єру",
  "flow.develop": "РОЗВИВАЙТЕ",
  "flow.develop.detail": "свої компетенції",
  "tools.kicker": "ОБЕРІТЬ СВІЙ ШЛЯХ",
  "tools.title": "Чим би ви хотіли займатися? ",
  "tools.body": "Дослідіть мінливий ландшафт природничих наук або оцініть навички, що відповідають вашим кар'єрним інтересам.",
  "tools.radar.kicker": "01 · ДОСЛІДЖЕННЯ",
  "tools.radar.title": "Радар навичок та трендів",
  "tools.radar.body": "Відкрийте для себе новітні технології, навички та кар'єрні шляхи в біології та хімії та дослідіть, як вони пов'язані.",
  "tools.radar.m1": "Новітні тенденції",
  "tools.radar.m2": "Зв'язки навичок",
  "tools.radar.m3": "Кар'єрні шляхи",
  "tools.radar.cta": "Дослідіть радар",
  "tools.match.kicker": "02 · ОЦІНКА",
  "tools.match.title": "Суміщення кар'єри та навичок",
  "tools.match.body": "Оберіть професійний шлях, оцініть свої поточні компетенції та визначте навички, які є найважливішими для вашого розвитку.",
  "tools.match.m1": "Оцінювання 3–5 хв.",
  "tools.match.m2": "Профіль особистих навичок",
  "tools.match.m3": "Рекомендації щодо навчання",
  "tools.match.cta": "Оцінити мої навички",
  "discover.eyebrow": "Інтерактивний попередній перегляд",
  "discover.title": "Перегляньте, як наукові галузі пов'язані",
  "discover.body": "Перегляньте спрощений вигляд каталогу, а потім відкрийте повний радар, щоб дослідити кожен зв'язок.",
  "discover.tabs": "Дисципліна",
  "discover.area": "Вибрана область",
  "discover.cta": "Дослідити повний радар",
  "model.eyebrow": "Модель знань",
  "model.title": "Як Обсерваторія поєднує навчання та роботу",
  "model.body": "Каталог пов'язує освіту, компетенції та наукові технології з кар'єрними шляхами та актуальністю на ринку праці, а потім з навчанням BOOSTSCIENCE.",
  "model.education": "Освіта",
  "model.skills": "Навички",
  "model.technologies": "Технології",
  "model.careers": "Кар'єрні шляхи",
  "model.labour": "Актуальність на ринку праці",
  "model.gap": "Нестача навичок",
  "model.learning": "Навчання BOOSTSCIENCE",
  "model.development": "Розвиток навичок",
  "metrics.eyebrow": "Знімок каталогу",
  "metrics.title": "Що відображається сьогодні",
  "evidence.eyebrow": "Прозорий дизайн",
  "evidence.title": "Докази, що лежать в основі Обсерваторії",
  "evidence.body": "Кожен опублікований запис визначає його джерело, період відліку та останній огляд. Обсерваторія структурована, відстежувана та розроблена для розвитку разом з BOOSTSCIENCE.",
  "evidence.traceable": "Відстежуваність",
  "evidence.traceable.body": "Джерела, дати огляду та рівні доказів знаходяться в кожній опублікованій сутності.",
  "evidence.structured": "Структурована",
  "evidence.structured.body": "Дисципліни, технології, навички, кар'єри та ресурси мають одну модель даних.",
  "evidence.evolving": "Розвиток",
  "evidence.evolving.body": "Каталог переглядається в міру зростання доказів проекту та досвіду партнерів.",
  "evidence.cta": "Дослідження методології",
  "learn.eyebrow": "Навчання BOOSTSCIENCE",
  "learn.title": "Від дослідження до розвитку навичок",
  "learn.body": "Використовуйте Обсерваторію, щоб виявити пріоритетні області, оцінити свій профіль та слідкувати за ресурсами BOOSTSCIENCE – опублікованими результатами зараз, посиланнями на модулі та віртуальні лабораторії, коли вони будуть опубліковані.",
  "learn.discover": "Відкриття",
  "learn.discover.body": "Досліджуйте нові області, навички та кар'єрні шляхи в Радарі.",
  "learn.assess": "Оцінка",
  "learn.assess.body": "Створіть профіль відповідності навичок вибраному шляху.",
  "learn.develop": "Розробка",
  "learn.develop.body": "Дотримуйтесь рекомендованих навчальних заходів BOOSTSCIENCE для виявлених прогалин.",
  "learn.soon": "Посилання на модулі, віртуальні лабораторії та MOOC з'являтимуться тут, коли BOOSTSCIENCE їх публікуватиме. Публічні результати D1 вже доступні.",
  "learn.available": "Доступно зараз",
  "learn.coming": "У розробці",
  "learn.open": "Відкрито",
  "context.eyebrow": "Контекст проекту",
  "context.title": "Створено для BOOSTSCIENCE, а не як універсальний віджет навичок",
  "context.body": "Каталог відповідає чотирьом стовпам проекту — Розуміння, Проектування, Розробка, Реалізація — та консорціуму з 12 організацій у Греції, Іспанії та Україні, включаючи п'ять українських університетів та партнерів у фармацевтичній галузі, сфері розробки ліків та досліджень матеріалів.",
  "context.m1": "Магістерський модуль з прикладного хімічного та біомолекулярного аналізу",
  "context.m2": "Віртуальні лабораторії та проведення VLE / MOOC",
  "context.m3": "Шляхи розвитку університету в галузі фармацевтики, біотехнологій та матеріалів",
  "method.pillars.title": "Методологія проекту",
  "method.pillars.body": "BOOSTSCIENCE організований за принципами: Розуміння (аналіз потреб), Проектування (навчальна програма), Розробка (VLE, цифровий контент та віртуальні лабораторії) та Виконання (пілотний проект, нарощування потенціалу та сталий розвиток). Обсерваторія наразі підтримує програму «Розуміння та проектування» за допомогою таксономії, курованої експертами. Дані опитування WP2 ще не імпортовані.",
  "method.pillar.understand": "Розуміння",
  "method.pillar.understand.body": "Сформулювати карту потреб у вищій освіті з біології та хімії, включаючи українські університети-партнери, та структурувати ландшафт навичок, який публікує Обсерваторія.",
  "method.pillar.design": "Проектування",
  "method.pillar.design.body": "Інформування про розробку навчальної програми та компетенцій для магістерського модуля з прикладного хімічного та біомолекулярного аналізу.",
  "method.pillar.develop": "Розробка",
  "method.pillar.develop.body": "Підготовка цифрового контенту, віртуальних лабораторій та проведення VLE / MOOC. Посилання Обсерваторії на ці заходи з'являються, коли партнери їх публікують.",
  "method.pillar.deliver": "Виконання",
  "method.pillar.deliver.body": "Підтримка пілотних проектів, нарощування потенціалу та сталого розвитку. Обсерваторія залишається інструментом дослідження, а не живою інформаційною панеллю ринку праці.",
  "final.title": "Початок дослідження ландшафту навичок",
  "final.body": "Відкрийте Radar, щоб переглянути зв'язки, або пройдіть короткий Skills Match, щоб визначити пріоритети розвитку.",
  "method.heading": "Обґрунтований доказами, прозорий та динамічний",
  "method.body": "Обсерваторія структурує інформацію з дослідницької діяльності BOOSTSCIENCE, партнерів проекту, рамок компетенцій та вибраних зовнішніх джерел. Кожен набір даних визначає його джерело, період відліку та останній огляд.",
  "method.cta": "Переглянути методологію",
  "footer.notice": "Розроблено в рамках BOOSTSCIENCE — Підвищення якості освіти та підприємницького мислення для природничих наук в Україні.",
  "radar.title": "Радар навичок та трендів",
  "radar.body": "Дослідіть технології, навички та професійні шляхи, що стосуються біології та хімії. Виберіть запис, щоб перевірити зв'язки, докази та пов'язане навчання.",
  "radar.search": "Пошук навичок, технологій або кар'єри",
  "radar.viewRadar": "Карта",
  "radar.viewList": "Список",
  "radar.connections": "Показати зв'язки",
  "radar.filters": "Фільтри",
  "radar.clear": "Очистити все",
  "radar.discipline": "Дисципліна",
  "radar.view": "Вигляд",
  "radar.trend": "Тренд",
  "radar.geo": "Географія",
  "radar.all": "Усі",
  "radar.biology": "Біологія",
  "radar.chemistry": "Хімія",
  "radar.interdisciplinary": "Міждисциплінарні",
  "radar.technologies": "Технології",
  "radar.skills": "Навички",
  "radar.careers": "Кар'єра",
  "radar.sectors": "Сектори",
  "radar.emerging": "Нові",
  "radar.growing": "Зростаючі",
  "radar.established": "Сталі",
  "radar.transforming": "Трансформуючі",
  "radar.europe": "Європа",
  "radar.ukraine": "Україна",
  "radar.geoAll": "Усі доступні",
  "radar.core": "Основні",
  "radar.ringGrowing": "Зростаючі",
  "radar.ringEmerging": "Нові",
  "radar.empty": "Жоден запис не відповідає вибраним фільтрам. ",
  "radar.searchEmpty": "Не знайдено відповідних навичок, технологій чи кар'єр. Спробуйте ширший пошук.",
  "radar.close": "Закрити деталі",
  "radar.why": "Чому це важливо",
  "radar.relatedSkills": "Супутні навички",
  "radar.relatedCareers": "Супутні кар'єри",
  "radar.relatedSectors": "Відповідні сектори",
  "radar.relatedTech": "Супутні технології",
  "radar.connection": "Зв'язок з BOOSTSCIENCE",
  "radar.evidence": "Докази",
  "radar.source": "Джерело",
  "radar.period": "Період відліку",
  "radar.coverage": "Географічне охоплення",
  "radar.reviewed": "Останній перегляд",
  "radar.assess": "Оцінити мої навички для супутніх кар'єр",
  "radar.exploreMatch": "Відкрити зіставлення кар'єр та навичок",
  "radar.col.name": "Назва",
  "radar.col.type": "Тип",
  "radar.col.discipline": "Дисципліна",
  "radar.col.trend": "Тренд",
  "radar.col.skills": "Супутні навички",
  "radar.col.skillsHint": "Для навичок, інші навички каталогу в цьому поданні, які мають спільну кар'єру або технологію (ті ж посилання, що й на карті). Тенденція та пошук не зменшують цю кількість. Для технологій, кар'єр та секторів, супутні навички, заявлені в каталозі.",
  "radar.col.careers": "Супутні кар'єри",
  "radar.col.source": "Докази / джерело",
  "radar.metric.skills": "Навігація навичок",
  "radar.metric.careers": "Шляхи кар'єри",
  "radar.metric.emerging": "Нові галузі",
  "radar.metric.sources": "Переглянуті джерела",
  "radar.metric.resources": "Опубліковані ресурси",
  "radar.group": "Група навичок",
  "radar.dense": "Мітки приховані, поки це подання переповнене — відкрити запис або перейти до списку.",
  "radar.count": "{n} записів",
  "radar.viewMode": "Режим перегляду",
  "radar.ringGuide": "Концентричні кільця від центру: ядро ​​(установлене), зростаюче, потім нове.",
  "radar.catalogNote": "Цифри – це підрахунки з опублікованого каталогу Обсерваторії. Вони не є актуальною статистикою ринку праці.",
  "radar.mobileList": "На цьому екрані використовується вигляд списку, щоб записи залишалися читабельними.",
  "radar.help.title": "Як користуватися радаром",
  "radar.help.body": "Виберіть вигляд, потім виберіть вузол або рядок списку. Панель показує визначення, пов'язані навички та кар'єри, а також докази, що стоять за цим записом.",
  "radar.help.filter": "Використовуйте Дисципліну, Вигляд, Групу навичок, Тенденцію та Географію, щоб звузити область пошуку.",
  "radar.help.select": "Поки що нічого не вибрано.",
  "match.title": "Зіставлення кар'єри та навичок",
  "match.body": "Виберіть професійний шлях і пройдіть коротку самооцінку, щоб визначити сильні сторони та пріоритетні компетенції.",
  "match.start": "Розпочати оцінку",
  "match.disclaimer": "Освітнє керівництво на основі самооцінки. Не є тестом з набору персоналу, формальною кваліфікацією чи професійною сертифікацією.",
  "privacy.notice.title": "Конфіденційність та ваш профіль Обсерваторії",
  "privacy.notice.body": "За вашої згоди BOOSTSCIENCE зберігає вашу електронну пошту та контекст Обсерваторії, необхідні для продовження вашої подорожі. Згода на розсилку є окремою та необов'язковою. Ми не зберігаємо імена, номери телефонів, резюме, ідентифікатори університетів або індивідуальні відповіді на запитання щодо навичок 0–4. Якщо ви пізніше створите обліковий запис, для отримання зберігатимуться лише вибраний шлях, бали та згенерований профіль.",
  "privacy.notice.link": "Політика конфіденційності",
  "match.privacy": "Ваша електронна адреса зберігається за вашою згодою в рамках подорожі Обсерваторією. Згода на розсилку є окремою та необов'язковою. Індивідуальні відповіді на запитання щодо навичок 0-4 залишаються у вашому браузері та не зберігаються як дані потенційних клієнтів.",
  "match.startOver": "Почати спочатку",
  "match.continue": "Продовжити",
  "match.back": "Назад",
  "match.seeResults": "Переглянути результати",
  "match.step.field": "Галузь",
  "match.step.career": "Кар'єра",
  "match.step.skills": "Навички",
  "match.step.priorities": "Пріоритети",
  "match.step.results": "Результати",
  "match.field.q": "Яка ваша основна галузь?",
  "match.stage.q": "Який етап найкраще вас описує? (необов'язково)",
  "match.stage.note": "Це коригує запропоновані шляхи (наприклад, Академічний персонал). Це не змінює розрахунок відповідності навичок.",
  "match.stage.masters-student": "Магістрант",
  "match.stage.recent-graduate": "Нещодавній випускник",
  "match.stage.doctoral-candidate": "Докторант",
  "match.stage.researcher": "Дослідник",
  "match.stage.academic-staff": "Академічний персонал",
  "match.stage.other": "Інше",
  "match.career.q": "Яка професійна галузь вас найбільше цікавить?",
  "match.career.examples": "Приклади шляхів",
  "match.career.select": "Виберіть шлях для оцінки",
  "match.skills.q": "Як би ви оцінили ці компетенції сьогодні?",
  "match.skills.help": "Оцінюйте одну компетенцію за раз від 0 (ще не знайомий) до 4 (сильний / можу застосовувати самостійно). Ваш прогрес зберігається в цьому браузері під час завершення оцінювання.",
  "match.scale.0": "Ще не знайомий",
  "match.scale.1": "Базова обізнаність",
  "match.scale.2": "Розвиток",
  "match.scale.3": "Впевнений",
  "match.scale.4": "Сильний / самостійний",
  "match.incomplete": "Будь ласка, оцініть кожну перелічену навичку, перш ніж продовжити.",
  "match.priorities.q": "Що б ви найбільше хотіли посилити?",
  "match.priorities.help": "Виберіть до трьох основних сфер. Це персоналізує рекомендації та не замінює розрахунок відповідності навичок.",
  "match.results.title": "Ваш профіль навичок BOOSTSCIENCE",
  "match.results.pathway": "Вибраний шлях",
  "match.results.score": "Відповідність навичок",
  "match.results.band.strong": "Сильна відповідність",
  "match.results.band.good": "Хороша основа",
  "match.results.band.developing": "Розвиток відповідності",
  "match.results.band.priority": "Визначені пріоритетні сфери розвитку",
  "match.results.lead.strong": "Ваша самооцінка вказує на сильну відповідність цьому шляху розвитку всіх розглянутих компетенцій.",
  "match.results.lead.good": "Ваша самооцінка вказує на міцну основу для цього шляху, причому вибрані компетенції пропонують найсильніші можливості для розвитку.",
  "match.results.lead.developing": "Ваша самооцінка вказує на розвиток відповідності. Кілька компетенцій є корисними пріоритетами для подальшої практики.",
  "match.results.lead.priority": "Ваша самооцінка виділяє пріоритетні сфери розвитку для цього шляху. Розглядайте наведений нижче список як порядок денний навчання, а не як оцінку потенціалу.",
  "match.results.strengths": "Поточні сильні сторони",
  "match.results.strengths.body": "Ці компетенції порівняно добре узгоджуються з обраним шляхом на основі вашої самооцінки.",
  "match.results.priorities": "Пріоритетні навички для розвитку",
  "match.results.related": "Супутні кар'єрні шляхи",
  "match.results.path": "Ваш навчальний шлях BOOSTSCIENCE",
  "match.results.sequence": "Рекомендована послідовність розвитку",
  "match.results.print": "Друк / Зберегти як PDF",
  "match.results.radar": "Дослідити радар навичок",
  "match.results.exploreSkill": "Дослідити в радар",
  "match.comingSoon": "Скоро",
  "match.noResources": "Навчальні ресурси BOOSTSCIENCE, пов'язані з цією навичкою, будуть додані, коли вміст модуля стане доступним.",
  "match.priority.high": "Високий пріоритет",
  "match.priority.medium": "Середній пріоритет",
  "match.noVacancies": "Це не є актуальним списком вакансій.",
  "method.pageTitle": "Методологія обсерваторії",
  "method.purpose.title": "Мета",
  "method.purpose.body": "Обсерваторія підтримує дослідження навичок, технологій та кар'єрних шляхів, що стосуються біології та хімії в BOOSTSCIENCE. Вона допомагає магістерам, викладачам та партнерам бачити зв'язки між вищою освітою, компетенціями та галузями, що відповідають ринку праці, без необхідності вимагати актуальних даних про зайнятість.",
  "method.model.title": "Модель даних",
  "method.model.body": "Інформація структурована як дисципліна – домен – технологія – навичка – кар'єра – навчальний ресурс, з джерелами, пов'язаними з кожною опублікованою сутністю.",
  "method.framework.title": "Структура навичок",
  "method.framework.body": "Таксономія базується на дослідженнях проекту BOOSTSCIENCE, досвіді партнерів та вибраних структурах компетенцій: ResearchComp, DigComp, EntreComp та GreenComp. Зіставлення узгоджуються з цими рамками або базуються на них. Вони не є сертифікаціями та не означають офіційного схвалення.",
  "method.scoring.title": "Зіставлення навичок",
  "method.scoring.body": "Результати генеруються на основі зіставлення кар'єрних навичок, вагових коефіцієнтів важливості (1–5), цільових рівнів (1–4) та вашої самооцінки (0–4). Для кожної навички achievementRatio = min(userLevel / targetLevel, 1). Збіг навичок – це середньозважене за важливістю значення цих коефіцієнтів, виражене у відсотках. Це не ймовірність працевлаштування чи оцінка найму.",
  "method.limits.title": "Обмеження",
  "method.limits.body": "Самооцінка є суб'єктивною. Умови ринку праці змінюються. Інформацію слід періодично переглядати. Результати є освітніми рекомендаціями, а не професійною сертифікацією, і не гарантують працевлаштування. Мітки трендів – це якісні класифікації, а не статистика попиту.",
  "method.sources.title": "Джерела даних",
  "method.update.title": "Останній перегляд",
  "method.update.body": "Каталог востаннє переглядався: {date}.",
  "error.load": "Нам не вдалося завантажити цю інформацію. Спробуйте ще раз.",
  "error.loading": "Завантаження каталогу обсерваторії…",
  "error.retry": "Повторити спробу",
  "teaser.eyebrow": "ОБСЕРВАТОРІЯ BOOSTSCIENCE",
  "teaser.heading": "Дослідіть майбутнє навичок у біології та хімії",
  "teaser.body": "Відкрийте для себе новітні технології, дослідіть кар'єрні шляхи та визначте компетенції, що формують природничі науки.",
  "teaser.cta": "Дослідіть обсерваторію",
  "teaser.match": "Оцініть мої навички",
  "a11y.chart": "Поточний вигляд: {discipline}, {trend} {type}. Відображено {count} записів. {connected}",
  "group.scientific-lab": "Наукові та лабораторні",
  "group.digital-data": "Цифрові технології та дані",
  "group.research": "Дослідження",
  "group.green-sustainability": "Екологічність та сталий розвиток",
  "group.entrepreneurial-innovation": "Підприємництво та інновації",
  "group.transferable-professional": "Переносні та професійні",
  "account.login": "Увійти",
  "account.logout": "Вийти",
  "account.create": "Створити обліковий запис",
  "account.myResults": "Мої результати",
  "account.myResults.body": "Створіть обліковий запис або увійдіть за допомогою Google, щоб отримати профілі навичок, які ви зберегли після Career & Skills Match, та завантажити відформатований PDF-файл. Ми зберігаємо ідентифікатор входу лише для цього отримання.",
  "account.email": "Електронна пошта",
  "account.password": "Пароль (8+ символів)",
  "account.mode.label": "Обліковий запис",
  "account.consent": "Я погоджуюся з тим, що BOOSTSCIENCE зберігає ідентифікатор входу (електронну пошту або ідентифікатор облікового запису Google) та цей профіль навичок (вибраний шлях та бали), щоб я міг отримати його та створити PDF-файл. Див.",
  "account.consent.required": "Будь ласка, підтвердьте згоду на конфіденційність, перш ніж продовжити.",
  "account.privacyLink": "політика конфіденційності",
  "account.google": "Продовжити за допомогою Google",
  "account.google.or": "або використовуйте електронну пошту та пароль",
  "account.google.setup": "Вхід Google ще не підключено. Використайте електронну пошту та пароль або попросіть адміністратора сайту додати ідентифікатор клієнта Google у Налаштуваннях – Обсерваторія.",
  "account.google.cancelled": "Вхід до Google скасовано.",
  "account.save.title": "Зберегти цей профіль навичок",
  "account.save.body": "Вашу електронну адресу вже отримано для цієї подорожі в Обсерваторії. Створення облікового запису або вхід за допомогою Google необов'язкові та потрібні лише в тому випадку, якщо ви хочете пізніше отримати збережені профілі.",
  "account.save.loggedIn": "Ви ввійшли як {email}. Збережіть цей профіль, щоб завантажити його знову пізніше.",
  "account.save.action": "Зберегти профіль",
  "account.save.done": "Збережено. Відкрийте відформатований профіль, щоб завантажити або роздрукувати PDF.",
  "account.downloadPdf": "Завантажити PDF",
  "account.signedIn": "Ви ввійшли як {email}.",
  "account.empty": "Поки що немає збережених профілів навичок. Заповніть «Зіставлення кар'єри та навичок», а потім збережіть на сторінці результатів.",
  "account.error": "Щось пішло не так. Будь ласка, спробуйте ще раз.",
  "account.unavailable": "Збереження облікового запису доступне в опублікованій Обсерваторії на boostscience.eu.",
  "account.print.kicker": "Обсерваторія · «Зіставлення кар'єри та навичок»",
  "account.print.disclaimer": "Освітнє керівництво на основі самооцінки. Не є тестом з рекрутингу, офіційною кваліфікацією чи професійною сертифікацією. «Зіставлення навичок» – це зважене за важливістю узгодження ваших оцінок з обраним шляхом. Це не ймовірність працевлаштування чи оцінка найму. Ми не зберігаємо імена, номери телефонів, резюме, ідентифікатори університетів або відповіді на запитання про навички від 0 до 4. Ідентифікатор входу зберігається лише в тому випадку, якщо ви створили обліковий запис.",
  "flow.radar.aria": "Хід налаштування радара",
  "flow.radar.title": "Радар навичок та тенденцій",
  "flow.radar.step": "Крок {n} з 4",
  "flow.radar.hero": "Дослідіть ландшафт природничих наук",
  "flow.radar.hero.body": "Виберіть дисципліну, яку ви хочете дослідити спочатку. Ви можете змінити її пізніше всередині радара.",
  "flow.radar.choose": "Виберіть, що дослідити",
  "flow.radar.choose.q": "Що ви хочете побачити спочатку?",
  "flow.radar.skills.body": "Компетенції, що використовуються в наукових та професійних напрямках.",
  "flow.radar.technologies.body": "Наукові та цифрові галузі, що змінюють біологію та хімію.",
  "flow.radar.careers.body": "Професійні напрямки та пов'язані з ними компетенції.",
  "flow.radar.objective": "Ваша мета",
  "flow.radar.objective.q": "Що було б для вас найкориснішим?",
  "flow.radar.intent.trends": "Зрозумійте нові тенденції",
  "flow.radar.intent.career": "Дослідіть кар'єрні шляхи",
  "flow.radar.intent.skills": "Визначте відповідні навички",
  "flow.radar.ready": "Ваш радар готовий",
  "flow.radar.ready.body": "Введіть свою електронну адресу, щоб відкрити персоналізоване переглядання Radar та підтримувати зв'язок з вашою подорожжю в Обсерваторію BOOSTSCIENCE.",
  "flow.match.aria": "Хід оцінювання",
  "flow.match.title": "Кар'єра та підбір навичок",
  "flow.match.step": "Крок {current} з {total}",
  "flow.match.hero": "Створіть свій профіль навичок у галузі природничих наук",
  "flow.match.hero.body": "Спочатку оберіть свою галузь. Ми адаптуємо шляхи та питання до ваших інтересів.",
  "flow.match.about": "Про вас",
  "flow.match.about.q": "На якому етапі вашої професійної кар'єри ви знаходитесь?",
  "flow.match.about.body": "Це лише покращує пропозиції шляхів. Це не змінює розрахунок відповідності навичок.",
  "flow.match.direction": "Напрямок кар'єри",
  "flow.match.direction.q": "Яка професійна галузь вас найбільше цікавить?",
  "flow.match.pathway": "Виберіть шлях",
  "flow.match.ready": "Ваш профіль формується",
  "flow.match.ready.body": "Введіть свою електронну адресу, щоб продовжити оцінювання та підтримувати зв'язок з вами з вашим персоналізованим профілем навичок BOOSTSCIENCE.",
  "flow.match.competency": "Компетенція {current} з {total}",
  "flow.match.competency.q": "Наскільки впевнено ви застосовуєте цю компетенцію?",
  "flow.match.toPriorities": "Перейти до пріоритетів",
  "flow.match.final": "Останній крок",
  "flow.match.viewProfile": "Переглянути мій профіль",
  "flow.match.profileEyebrow": "ВАШ ПРОФІЛЬ НАВИЧОК BOOSTSCIENCE",
  "lead.step": "Ваш персоналізований досвід готовий",
  "lead.continueTitle": "Продовжити з вашою електронною адресою",
  "lead.continueBody": "Введіть свою електронну адресу, щоб продовжити та підтримувати зв'язок з вами з вашою подорожжю в Обсерваторію BOOSTSCIENCE.",
  "lead.email": "Електронна пошта",
  "lead.placeholder": "you@example.com",
  "lead.privacy": "Я погоджуюся з тим, що BOOSTSCIENCE обробляє мою електронну пошту для надання та зберігання інформації про мою подорож до Обсерваторії. Див. політику конфіденційності.",
  "lead.marketing": "Я також хотів(ла) би отримувати новини BOOSTSCIENCE та оновлення Обсерваторії.",
  "lead.error.consent": "Будь ласка, підтвердьте згоду на конфіденційність, щоб продовжити.",
  "lead.error.email": "Введіть дійсну адресу електронної пошти.",
  "lead.error.save": "Нам не вдалося зберегти вашу електронну пошту. Спробуйте ще раз.",
  "lead.saving": "Збереження…",
  "lead.note": "Пароль не потрібен. Згода на розсилку новин необов’язкова.",
};

let locale = "en";
const dictionaries: Record<string, Dict> = { en, uk };

export function t(key: string, vars: Record<string, string | number> = {}): string {
  const table = dictionaries[locale] ?? en;
  let value = table[key] ?? en[key] ?? key;
  for (const [k, v] of Object.entries(vars)) {
    value = value.replaceAll(`{${k}}`, String(v));
  }
  return value;
}

export function getLocale(): string {
  return locale;
}

export function setLocale(next: string): void {
  locale = next in dictionaries ? next : "en";
}
